import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  LinearProgress,
  SvgIcon,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Autocomplete,
  TextField,
  InputAdornment,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import React, { useState } from "react";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import DeleteIcon from "@mui/icons-material/Delete";
import { ReactComponent as EditIcon } from "../../../assets/icons/edit.svg";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  StyledSelect,
  StyledTextField,
} from "../../customComponents/styled/styledInputs";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import ClassDialog from "./ClassDialog";
import { useGetSubSectionsPaginatedQuery } from "../settings/sections/sectionsApiSlice";
import { useGetTeachersPaginatedQuery } from "../staff/staffApiSlice";
import {
  useAddTeacherToClassroomMutation,
  useCreateClassroomMutation,
  useDeleteClassroomMutation,
  useGetClassesQuery,
  useGetClassesPaginatedQuery,
} from "./classApiSlice";
import toast from "../../../helpers/toast";
import CustomWarningDialog from "../../customComponents/CustomWarningDialog";

const classFormValidation = yup.object({
  name: yup.string().required("Required"),
  description: yup.string(),
  level: yup.string().required("Required"),
  subsection: yup.string().required("Required"),
  formMaster: yup.string().required("Required"),
});

function StudentCount({ id }) {
  const { data } = useGetClassesQuery(`${id}/students`);
  return data?.length ?? 0;
}

function Classes() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [focusedClass, setFocusedClass] = useState(null);
  const [openClassDialog, setOpenClassDialog] = useState(false);
  const [openWarningDialog, setOpenWarningDialog] = useState(false);

  const [subsectionSearch, setSubsectionSearch] = useState("");
  const [teacherSearch, setTeacherSearch] = useState("");
  const [classPage, setClassPage] = useState(0);
  const [classPerPage, setClassPerPage] = useState(10);
  const [classSearch, setClassSearch] = useState("");

  const { data: subsectionsData } = useGetSubSectionsPaginatedQuery({
    page: 1,
    per_page: 10,
    q: subsectionSearch,
  });
  const subsections = subsectionsData?.subsections ?? [];
  const { data: teachersData } = useGetTeachersPaginatedQuery({
    page: 1,
    per_page: 10,
    q: teacherSearch,
  });
  const teachers = teachersData?.teachers ?? [];

  const { data: classroomsData, isLoading: getClassRoomsLoading } =
    useGetClassesPaginatedQuery({
      page: classPage + 1,
      per_page: classPerPage,
      q: classSearch,
    });
  const classrooms = classroomsData?.classrooms ?? [];
  const totalClasses = classroomsData?.pagination?.total_items ?? 0;

  const [createClass, { isLoading: createClassLoading }] =
    useCreateClassroomMutation();

  const [addTeacherToClass, { isLoading: addTeacherToClassLoading }] =
    useAddTeacherToClassroomMutation();

  const [deleteClass] = useDeleteClassroomMutation();

  const classForm = useFormik({
    initialValues: {
      name: "",
      description: "",
      level: "",
      subsection: "",
      formMaster: "",
    },
    enableReinitialize: true,
    validationSchema: classFormValidation,
    onSubmit: async (values) => {
      const body = {
        name: values.name,
        description: values.description,
        level: values.level,
        subsection_id: values.subsection,
      };
      try {
        const response = await createClass(body).unwrap();
        classForm.resetForm();
        if (response.id) {
          toast.success("Class created successfully");
          const addTeacherBody = {
            class_Id: response.id,
            user_id: values.formMaster,
          };
          const res = await addTeacherToClass(addTeacherBody).unwrap();

          if (res) {
            toast.success("Teacher added successfully");
          } else {
            toast.error("Teacher not added");
          }
        } else {
          toast.error("Class already exists");
        }
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      }
    },
  });

  const handleDelete = async () => {
    try {
      const response = await deleteClass(focusedClass).unwrap();
      if (response) {
        toast.success("Class deleted successfully");
      }
    } catch (error) {}
  };

  return (
    <>
      {/* modals */}
      <CustomWarningDialog
        open={openWarningDialog}
        setOpen={setOpenWarningDialog}
        item={focusedClass}
        action={handleDelete}
        message="Action is irreversable, are you sure you want to delete?"
      />
      <ClassDialog
        selectedClass={focusedClass}
        setSelectedClass={setFocusedClass}
        open={openClassDialog}
        setOpen={setOpenClassDialog}
      />
      <Grid container columnSpacing={3}>
        <Grid item xs={12} md={4} lg={4}>
          <StyledPaper>
            <Box className="paperHeader">
              <Typography variant="paperHeading">New Class Form</Typography>
            </Box>
            <Box className="paperBody">
              <form onSubmit={classForm.handleSubmit}>
                <Grid container columnSpacing={1} rowSpacing={2}>
                  <Grid item xs={12}>
                    <FormControl error fullWidth>
                      <Typography variant="formSubHeading">
                        Name of class
                      </Typography>
                      <StyledTextField
                        autoFocus
                        variant="outlined"
                        id="name"
                        name="name"
                        value={classForm.values.name}
                        onChange={classForm.handleChange}
                        error={
                          classForm.touched.name &&
                          Boolean(classForm.errors.name)
                        }
                      />
                      <FormHelperText>
                        {classForm.touched.name && classForm.errors.name}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  {/* <Grid item xs={12}>
                    <FormControl error fullWidth>
                      <Typography variant="formSubHeading">
                        Description
                      </Typography>
                      <StyledTextField
                        autoFocus
                        variant="outlined"
                        id="description"
                        name="description"
                        value={classForm.values.description}
                        onChange={classForm.handleChange}
                        error={
                          classForm.touched.description &&
                          Boolean(classForm.errors.description)
                        }
                      />
                      <FormHelperText>
                        {classForm.touched.description &&
                          classForm.errors.description}
                      </FormHelperText>
                    </FormControl>
                  </Grid> */}
                  <Grid item xs={12}>
                    <FormControl error fullWidth>
                      <Typography variant="formSubHeading">level</Typography>
                      <StyledTextField
                        autoFocus
                        variant="outlined"
                        id="level"
                        name="level"
                        value={classForm.values.level}
                        onChange={classForm.handleChange}
                        error={
                          classForm.touched.level &&
                          Boolean(classForm.errors.level)
                        }
                      />
                      <FormHelperText>
                        {classForm.touched.level && classForm.errors.level}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <Typography variant="formSubHeading">
                        sub-Section
                      </Typography>
                      <Autocomplete
                        options={subsections}
                        getOptionLabel={(option) => option.name || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        value={
                          subsections.find(
                            (s) => s.id === classForm.values.subsection,
                          ) || null
                        }
                        onChange={(_, value) =>
                          classForm.setFieldValue("subsection", value?.id || "")
                        }
                        onInputChange={(_, value, reason) => {
                          if (reason === "input") setSubsectionSearch(value);
                        }}
                        filterOptions={(x) => x}
                        renderInput={(params) => (
                          <StyledTextField
                            {...params}
                            placeholder="Search sub-section..."
                            error={
                              classForm.touched.subsection &&
                              Boolean(classForm.errors.subsection)
                            }
                          />
                        )}
                      />
                      <FormHelperText>
                        {classForm.touched.subsection &&
                          classForm.errors.subsection}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <Typography variant="formSubHeading">
                        form master
                      </Typography>
                      <Autocomplete
                        options={teachers}
                        getOptionLabel={(option) =>
                          option
                            ? `${option.first_name} ${option.last_name}`
                            : ""
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.user_id === value.user_id
                        }
                        value={
                          teachers.find(
                            (t) => t.user_id === classForm.values.formMaster,
                          ) || null
                        }
                        onChange={(_, value) =>
                          classForm.setFieldValue(
                            "formMaster",
                            value?.user_id || "",
                          )
                        }
                        onInputChange={(_, value, reason) => {
                          if (reason === "input") setTeacherSearch(value);
                        }}
                        filterOptions={(x) => x}
                        renderInput={(params) => (
                          <StyledTextField
                            {...params}
                            placeholder="Search teacher..."
                            error={
                              classForm.touched.formMaster &&
                              Boolean(classForm.errors.formMaster)
                            }
                          />
                        )}
                      />
                      <FormHelperText>
                        {classForm.touched.formMaster &&
                          classForm.errors.formMaster}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} textAlign={"right"}>
                    <StyledLoadingButton
                      loading={createClassLoading || addTeacherToClassLoading}
                      type="submit"
                    >
                      Submit
                    </StyledLoadingButton>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </StyledPaper>
        </Grid>
        <Grid item xs={12} md={8} lg={8}>
          <StyledPaper>
            <Box className="paperHeader">
              <Typography variant="paperHeading">Classes</Typography>
            </Box>
            <Box sx={{ px: 2, pb: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search classes..."
                value={classSearch}
                onChange={(e) => {
                  setClassSearch(e.target.value);
                  setClassPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {isMobile ? (
              <Box sx={{ p: 2 }}>
                {getClassRoomsLoading && <LinearProgress />}
                {classrooms?.map((row, i) => (
                  <Box
                    key={i}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "primary.main",
                        fontWeight: 600,
                        textTransform: "capitalize",
                        mb: 0.5,
                      }}
                      onClick={() => {
                        setFocusedClass(row);
                        setOpenClassDialog(true);
                      }}
                    >
                      {row.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Level: {row.level || "-"}
                    </Typography>
                    {/* <Typography variant="body2" color="text.secondary">
                      Description: {row.description || "-"}
                    </Typography> */}
                    <Typography variant="body2" color="text.secondary">
                      Students: <StudentCount id={row.id} />
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <SvgIcon
                        sx={{ color: "primary.safeOnLight", cursor: "pointer" }}
                        component={EditIcon}
                        inheritViewBox
                        onClick={() => {
                          setFocusedClass(row);
                          setOpenClassDialog(true);
                        }}
                      />
                      <DeleteIcon
                        className="tableCellLink"
                        onClick={() => {
                          setFocusedClass(row);
                          setOpenWarningDialog(true);
                        }}
                      />
                    </Stack>
                  </Box>
                ))}
              </Box>
            ) : (
              <TableContainer sx={{ pb: 1, overflowX: "auto" }}>
                {getClassRoomsLoading && <LinearProgress />}
                <Table sx={{ width: "100%", boxShadow: "none" }}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>class</StyledTableCell>
                      <StyledTableCell>level</StyledTableCell>
                      {/* <StyledTableCell>description</StyledTableCell> */}
                      <StyledTableCell>students</StyledTableCell>
                      <StyledTableCell align="left">edit</StyledTableCell>
                      <StyledTableCell align="left">delete</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {classrooms?.map((row, i) => (
                      <StyledTableRow key={i}>
                        <StyledTableCell component="th" scope="row">
                          <Box
                            className="tableCellLink"
                            onClick={() => {
                              setFocusedClass(row);
                              setOpenClassDialog(true);
                            }}
                            sx={{
                              textTransform: "capitalize",
                            }}
                          >
                            {row.name}
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell component="th" scope="row">
                          {row.level}
                        </StyledTableCell>
                        {/* <StyledTableCell component="th" scope="row">
                          {row.description}
                        </StyledTableCell> */}
                        <StyledTableCell component="th" scope="row">
                          <StudentCount id={row.id} />
                        </StyledTableCell>

                        <StyledTableCell align="left">
                          <SvgIcon
                            sx={{
                              color: "primary.safeOnLight",
                              cursor: "pointer",
                            }}
                            component={EditIcon}
                            inheritViewBox
                            onClick={() => {
                              setFocusedClass(row);
                              setOpenClassDialog(true);
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <DeleteIcon
                            className="tableCellLink"
                            onClick={() => {
                              setFocusedClass(row);
                              setOpenWarningDialog(true);
                            }}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <TablePagination
              component="div"
              count={totalClasses}
              page={classPage}
              onPageChange={(_, newPage) => setClassPage(newPage)}
              rowsPerPage={classPerPage}
              onRowsPerPageChange={(e) => {
                setClassPerPage(parseInt(e.target.value, 10));
                setClassPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{
                ".MuiTablePagination-toolbar": {
                  flexWrap: "wrap",
                  minWidth: 0,
                  px: 0,
                },
                ".MuiTablePagination-spacer": { display: "none" },
              }}
            />
          </StyledPaper>
        </Grid>
      </Grid>
    </>
  );
}

export default Classes;
